import type { ScoringZone, ShotEvaluation } from './sport-definition.interface';

/**
 * Shared zone-scoring utility for soccer and ice-hockey.
 *
 * Given the ball's final X/Y position relative to goal center and a set of
 * scoring zones, returns the highest-scoring zone whose bounds contain the
 * position, or null if no zone matches (i.e. a miss).
 */
export function resolveZone(
  finalX: number,
  finalY: number,
  zones: ScoringZone[]
): ScoringZone | null {
  // Sort descending by points so we always return the highest-value zone first
  // (handles overlapping zone definitions gracefully)
  const sorted = [...zones].sort((a, b) => b.points - a.points);
  return (
    sorted.find(
      (z) =>
        finalX >= z.bounds.xMin &&
        finalX <= z.bounds.xMax &&
        finalY >= z.bounds.yMin &&
        finalY <= z.bounds.yMax
    ) ?? null
  );
}

/**
 * Evaluate a goal-sport shot (soccer / ice-hockey) and return a ShotEvaluation.
 *
 * @param finalX                 Lateral position relative to goal center (scene units)
 * @param finalY                 Vertical position relative to goal base (scene units)
 * @param finalZ                 Depth at goal face (for finalPosition)
 * @param goalToleranceRadius    Max lateral distance from center that counts as on-target
 * @param goalHeight             Max vertical extent of the goal opening
 * @param zones                  Ordered scoring zone array
 * @param pointsPerSuccess       Fallback points when zones is empty
 * @param isWhiff                Pre-computed whiff flag
 */
export function evaluateGoalShot(params: {
  finalX: number;
  finalY: number;
  finalZ: number;
  goalToleranceRadius: number;
  goalHeight: number;
  zones: ScoringZone[];
  pointsPerSuccess: number;
  isWhiff: boolean;
}): ShotEvaluation {
  const { finalX, finalY, finalZ, goalToleranceRadius, goalHeight, zones, pointsPerSuccess, isWhiff } = params;

  if (isWhiff) {
    return {
      scored: false,
      pointsEarned: 0,
      zone: null,
      finalPosition: { x: 0, y: 0, z: 0 },
      isWhiff: true,
    };
  }

  // Check if ball is within the goal opening
  const inBounds =
    Math.abs(finalX) <= goalToleranceRadius && finalY >= 0 && finalY <= goalHeight;

  if (!inBounds) {
    return {
      scored: false,
      pointsEarned: 0,
      zone: null,
      finalPosition: { x: finalX, y: finalY, z: finalZ },
      isWhiff: false,
    };
  }

  const hitZone = resolveZone(finalX, finalY, zones);
  const pointsEarned = hitZone ? hitZone.points : pointsPerSuccess;

  return {
    scored: true,
    pointsEarned,
    zone: hitZone?.id ?? 'center',
    finalPosition: { x: finalX, y: finalY, z: finalZ },
    isWhiff: false,
  };
}
